import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import NotyfContext from "../../../contexts/NotyfContext";
import useWindowSize from "../../../hooks/useWindowSize";
import * as Yup from "yup";
import { Card } from "react-bootstrap";
import { Formik } from "formik";
import SimImportSettingsForm from "./SimImportSettingsForm";
import { importSim } from "../../../redux/slices/sims";
import useDeviceType from "../../../hooks/useDeviceType";

const SimImportSettings = ({ activeTab }) => {
  const { t } = useTranslation();
  const notyf = useContext(NotyfContext);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const initialValues = {
    provider: "",
    inputType: "",
    simType: "",
    iccid: "",
    file: null,
  };

  const validation = Yup.object().shape({
    provider: Yup.string().required("Required"),
    inputType: Yup.string().required("Required"),
    simType: Yup.string().required("Required"),
    iccid: Yup.string().when(["provider", "inputType"], {
      is: (provider, inputType) =>
        provider === "1NCE" && inputType === "single",
      then: Yup.string()
        .matches(
          /^(8988280666|89882280666|)\d{8,14}$/,
          "ICCID must start with 8988280666 or 89882280666 and be 19-23 digits long"
        )
        .required("ICCID is required for 1NCE when input type is single"),
      otherwise: Yup.string().nullable().notRequired(),
    }),
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t("settings.importSimSettings.title")}</Card.Title>
      </Card.Header>
      <Card.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validation}
          onSubmit={async (values, { setValues, setFieldError }) => {
            const data = {
              provider: values.provider,
              simType: values.simType,
            };

            if (values.inputType === "single") {
              data.iccid = values.iccid;
            } else if (values.inputType === "file") {
              data.file = values.file;
            }
            await importSim({ data })
              .then(() => {
                notyf.open({
                  type: "success",
                  message: "SIM cards successfully imported",
                  duration: 5000,
                  ripple: true,
                  dismissible: false,
                  position: {
                    x: "center",
                    y: "top",
                  },
                });
                setValues(initialValues);
              })
              .catch((error) => {
                if (values.provider === "Velos") {
                  setFieldError("iccid", error);
                } else if (values.provider === "1NCE") {
                  setFieldError("iccid", error);
                }
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <SimImportSettingsForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleSubmit={handleSubmit}
                isDesktop={isDesktop}
              />
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default SimImportSettings;
